import React from 'react';
import glamorous from 'glamorous';
import {WHITE, CHARCOAL, FOCUS_BLUE} from '../../../shared/style/colors';
import {PHONE, PHONE_LANDSCAPE, DESKTOP, LAPTOP} from '../../../shared/style/breakpoints';
import SimpleButton from '../../../shared/library/buttons/base/simple';
import RequestDemo from '../../enterprise/components/request-demo';
import {ALPHA} from '../../../shared/style/color-utils';
import FeatureCard from '../../../shared/library/cards/feature-card';
import {FONTS, WEIGHT} from '../../../shared/style/typography';
import {MAX_PAGE_WIDTH} from '../../../shared/style/dimensions';

const RequestDemoExtraStyle = {
  color: CHARCOAL,
  fontFamily: FONTS.CEREBRI_SANS_PRO,
  fontSize: 20,
  fontWeight: WEIGHT.ULTRA_BOLD,
  background: 'rgba(5,143,250, 0.03)',
  minWidth: 240,
  minHeight: 58,
  borderRadius: 4,
  marginLeft: 31,
  border: `1px solid ${CHARCOAL}`,
  padding: '13px 21px 15px 20px',
  '&:hover': {
    backgroundColor: 'rgba(5, 143, 250, 0.1)',
    color: CHARCOAL,
    border: `1px solid ${CHARCOAL}`
  },
  '&:focus': {
    border: `1px solid ${FOCUS_BLUE}`
  },
  [PHONE]: {
    margin: '18px 0 0'
  }
};

const Container = glamorous.div({
  display: 'block',
  marginBottom: 90,
  background: 'rgba(5,143,250, 0.03)',
  padding: '0 20px',
  [DESKTOP]: {
    padding: 0,
    marginBottom: 90
  },
  [PHONE]: {
    marginBottom: 70
  }
});

const Title = glamorous.div({
  fontFamily: FONTS.CEREBRI_SANS_PRO,
  fontStyle: 'normal',
  fontSize: 44,
  lineHeight: '53px',
  fontWeight: WEIGHT.ULTRA_BLACK,
  alignSelf: 'center',
  textAlign: 'center',
  [PHONE]: {
    fontSize: 28,
    lineHeight: '40px'
  },
  [LAPTOP]: {
    fontSize: 34
  }
});

const TitleTag = glamorous.h3({
  fontFamily: FONTS.CEREBRI_SANS_PRO,
  fontStyle: 'normal',
  fontWeight: WEIGHT.ULTRA_BLACK,
  fontSize: 21,
  textAlign: 'center',
  textTransform: 'uppercase',
  color: CHARCOAL,
  letterSpacing: '2px',
  marginTop: 0,
  marginBottom: 7,
  [PHONE]: {
    fontSize: 16
  },
  [LAPTOP]: {
    fontSize: 18
  }
});

const Content = glamorous.div({
  marginBottom: 40,
  '> svg': {
    width: 80,
    height: 80,
    marginBottom: 5
  },
  [DESKTOP]: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gridColumnGap: 40,
    textAlign: 'center',
    maxWidth: MAX_PAGE_WIDTH,
    margin: 'auto',
    marginBottom: 40
  },
  [PHONE]: {
    textAlign: 'center'
  }
});

const ExploreEnterprise = glamorous(SimpleButton)({
  minHeight: 58,
  background: CHARCOAL,
  border: 'none',
  borderRadius: 4,
  fontFamily: FONTS.CEREBRI_SANS_PRO,
  fontSize: 19,
  fontWeight: WEIGHT.ULTRA_BOLD,
  letterSpacing: 'normal',
  [DESKTOP]: {
    width: 373,
    height: 58
  },
  [PHONE_LANDSCAPE]: {
    marginLeft: 0
  },
  '&:hover': {
    backgroundColor: ALPHA(CHARCOAL, 0.9),
    border: `1px solid ${CHARCOAL}`
  },
  ':focus': {
    color: WHITE
  }
}).withComponent('a');

const TextContainer = glamorous.div({
  paddingTop: 112,
  maxWidth: MAX_PAGE_WIDTH,
  margin: 'auto',
  marginBottom: 70,
  [PHONE]: {
    paddingTop: 50,
    marginBottom: 65
  }
});

const ButtonContainer = glamorous.div({
  display: 'flex',
  justifyContent: 'center',
  paddingBottom: 120,
  [DESKTOP]: {
    maxWidth: MAX_PAGE_WIDTH,
    margin: 'auto'
  },
  [PHONE]: {
    display: 'block',
    paddingBottom: 75
  }
});

const options = [
  {
    icon: 'https://img.stackshare.io/fe/landing/technology_footprint.svg',
    title: 'Gain a fully automated 360° view of your technology footprint',
    description:
      'Real-time visibility and transparency gives you insight into who’s running what technologies, so you can improve collaboration and stay up to date on changes.',
    quoteText:
      'We have hundreds of different modules floating around in random GitHub repositories that nobody knows anything about. So StackShare for me, personally, makes it much easier to choose which module I like by looking up what I’m using now and doing a cross-comparison to see what other options are available.',
    authorDesc: 'Principal Software Engineer, Fortune 500 Company'
  },
  {
    icon: 'https://img.stackshare.io/fe/landing/technology_usage.svg',
    title: 'Standardize technology usage across your organization',
    description:
      'Tech radar capabilities enable you to create an approved set of tech stacks for different scenarios that everyone knows how to access and use.',
    quoteText:
      'StackShare helps us keep track of our technology decisions — which ones we want to adopt or retire or deprecate — and communicate the status to everyone, so people know what they should or shouldn’t use in their applications.',
    authorImage: 'https://img.stackshare.io/fe/landing/author_image2.svg',
    authorDesc: 'Chris Cummings, Senior Director of Architecture, Tyler Technologies'
  },
  {
    icon: 'https://img.stackshare.io/fe/landing/tech_scrawl.svg',
    title: 'Consolidate your tech stacks and rein in tech sprawl',
    description:
      'Reducing the number of competing technologies and vendors lowers your risk and costs, and helps you to pay down tech debt.',
    quoteText:
      'One of my big goals for using StackShare is to get a view of where we’re at and then use that to communicate why it’s important for us to be upgrading. So it’s partly a security story, partly a let’s tackle tech debt story. It also helps us have some standards and consolidation around things that most of the org knows.',
    authorDesc: 'Chief Architect, Series E Startup'
  },
  {
    icon: 'https://img.stackshare.io/fe/landing/team_speed.svg',
    title: 'Save time and increase development team speed',
    description:
      'Reusable and standardized components eliminates duplication of effort and allows your teams to focus on development, not infrastructure.',
    quoteText:
      'StackShare has saved us hundreds of hours compiling, maintaining, and updating information on our stacks. We can now track it all in real time.',
    authorImage: 'https://img.stackshare.io/fe/landing/author_image2.svg',
    authorDesc: 'Chris Cummings, Senior Director of Architecture, Tyler Technologies'
  }
];

const WhyEnterprise = () => {
  return (
    <Container background={WHITE}>
      <TextContainer>
        <TitleTag>Enterprise</TitleTag>
        <Title>Why teams use StackShare Enterprise</Title>
      </TextContainer>
      <Content>
        {options.map((item, index) => (
          <FeatureCard key={index} item={item} />
        ))}
      </Content>
      <ButtonContainer>
        <ExploreEnterprise href={`/enterprise`}>Explore StackShare Enterprise</ExploreEnterprise>
        <RequestDemo
          eventProperties={{
            index: 1,
            sectionName: 'SignupHeader'
          }}
          extraStyle={RequestDemoExtraStyle}
        />
      </ButtonContainer>
    </Container>
  );
};

export default WhyEnterprise;
